import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Receipt, Trash2, Pencil } from 'lucide-react';
import {
  Card, Button, Input, Select, Badge, EmptyState, Modal, Label, KPI, ConfirmDeleteModal,
} from '../components/ui';
import { useFirm } from '../contexts/FirmContext';
import { useAuth } from '../contexts/AuthContext';
import { listExpenses, createExpense, updateExpense, deleteExpense, listMatters } from '../lib/db';
import { formatDate, formatMoney } from '../lib/formatters';
import { useActivityLogger } from '../lib/useActivityLogger';
import toast from '../lib/toast';

const CATEGORIES = [
  'Filing Fee', 'Court Reporter', 'Expert Witness', 'Travel', 'Copying',
  'Postage', 'Process Server', 'Investigator', 'Deposition', 'Other',
];

const blank = () => ({
  description: '', amount: '', expense_date: new Date().toISOString().split('T')[0],
  category: 'Other', billable: true, matter_id: '',
});

export default function ExpensesPage() {
  const { firmId, refreshSignal } = useFirm();
  const { userProfile } = useAuth();
  const log = useActivityLogger();
  const [expenses, setExpenses] = useState([]);
  const [matters, setMatters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [billableFilter, setBillableFilter] = useState('');

  // Modal state — null = closed, 'new' = creating, expense obj = editing
  const [modalTarget, setModalTarget] = useState(null);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [deletingExpense, setDeletingExpense] = useState(null);

  const isEditing = modalTarget && modalTarget !== 'new';
  const modalOpen = modalTarget !== null;

  useEffect(() => {
    if (!firmId) { setLoading(false); return; }
    (async () => {
      try {
        const [{ data: exp }, { data: mat }] = await Promise.all([
          listExpenses(firmId),
          listMatters(firmId),
        ]);
        setExpenses(exp || []);
        setMatters(mat || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [firmId, refreshSignal]);

  const filtered = useMemo(() => expenses
    .filter((e) => billableFilter === '' ? true : e.billable === (billableFilter === 'true'))
    .filter((e) => !q || e.description?.toLowerCase().includes(q.toLowerCase()) || e.category?.toLowerCase().includes(q.toLowerCase())),
    [expenses, billableFilter, q]);

  const totals = useMemo(() => ({
    total:    expenses.reduce((s, e) => s + Number(e.amount || 0), 0),
    billable: expenses.filter((e) => e.billable).reduce((s, e) => s + Number(e.amount || 0), 0),
    unbilled: expenses.filter((e) => e.billable && !e.billed).reduce((s, e) => s + Number(e.amount || 0), 0),
  }), [expenses]);

  const openNew = () => {
    setForm(blank());
    setModalTarget('new');
  };

  const openEdit = (exp) => {
    setForm({
      description:  exp.description  || '',
      amount:       exp.amount       || '',
      expense_date: exp.expense_date || new Date().toISOString().split('T')[0],
      category:     exp.category     || 'Other',
      billable:     exp.billable     ?? true,
      matter_id:    exp.matter_id    || '',
    });
    setModalTarget(exp);
  };

  const closeModal = () => { setModalTarget(null); setForm(blank()); };

  const submit = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      amount:    parseFloat(form.amount),
      matter_id: form.matter_id || null,
    };

    if (isEditing) {
      const { data, error } = await updateExpense(modalTarget.id, payload);
      setSaving(false);
      if (error) { toast.error(error.message); return; }
      setExpenses((prev) => prev.map((e) => e.id === modalTarget.id ? { ...e, ...data } : e));
    } else {
      const { data, error } = await createExpense(firmId, { ...payload, user_id: userProfile?.id });
      setSaving(false);
      if (error) { toast.error(error.message); return; }
      log('expense', data.id, 'created', 'Expense recorded');
      setExpenses((prev) => [data, ...prev]);
    }
    closeModal();
  };

  const handleDeleteExpense = async () => {
    const { error } = await deleteExpense(deletingExpense.id);
    if (error) { toast.error(error.message); return; }
    setExpenses((prev) => prev.filter((e) => e.id !== deletingExpense.id));
    setDeletingExpense(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Expenses</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Track billable and non-billable costs</p>
        </div>
        <Button icon={Plus} onClick={openNew}>Log expense</Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KPI label="Total expenses" value={formatMoney(totals.total)}    tone="indigo" />
        <KPI label="Billable"       value={formatMoney(totals.billable)} tone="green" />
        <KPI label="Unbilled"       value={formatMoney(totals.unbilled)} tone="amber" />
      </div>

      <Card>
        <div className="flex flex-col sm:flex-row gap-2 p-4 border-b border-slate-100 dark:border-slate-800">
          <Input className="flex-1" placeholder="Search expenses…" value={q} onChange={(e) => setQ(e.target.value)} />
          <Select value={billableFilter} onChange={(e) => setBillableFilter(e.target.value)} options={[
            { value: '', label: 'All' },
            { value: 'true', label: 'Billable' },
            { value: 'false', label: 'Non-billable' },
          ]} />
        </div>

        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
        ) : !filtered.length ? (
          <EmptyState icon={Receipt} title="No expenses" description="Log costs to track and bill back to clients."
            action={<Button icon={Plus} onClick={openNew}>Log expense</Button>} />
        ) : (
          <>
            {/* Mobile cards */}
            <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
              {filtered.map((exp) => (
                <li key={exp.id} className="px-4 py-3 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{exp.description || '—'}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{exp.category}{exp.matter ? ` · ${exp.matter.matter_number}` : ''}</p>
                    <p className="text-xs text-slate-400">{formatDate(exp.expense_date)}</p>
                    <div className="mt-1">
                      <Badge tone={exp.billable ? (exp.billed ? 'slate' : 'green') : 'slate'}>
                        {exp.billable ? (exp.billed ? 'Billed' : 'Billable') : 'Non-billable'}
                      </Badge>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-mono font-semibold text-sm">{formatMoney(exp.amount)}</p>
                    <div className="flex items-center gap-2 justify-end mt-1">
                      <button onClick={() => openEdit(exp)} className="text-slate-400 hover:text-indigo-600"><Pencil size={13} /></button>
                      <button onClick={() => setDeletingExpense(exp)} className="text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                <tr>
                  <th className="text-left px-6 py-2 font-medium">Date</th>
                  <th className="text-left px-6 py-2 font-medium">Description</th>
                  <th className="text-left px-6 py-2 font-medium">Category</th>
                  <th className="text-left px-6 py-2 font-medium">Case File</th>
                  <th className="text-left px-6 py-2 font-medium">Billable</th>
                  <th className="text-right px-6 py-2 font-medium">Amount</th>
                  <th className="px-6 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filtered.map((exp) => (
                  <tr key={exp.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 group">
                    <td className="px-6 py-3 text-slate-500">{formatDate(exp.expense_date)}</td>
                    <td className="px-6 py-3 font-medium max-w-xs truncate">{exp.description || '—'}</td>
                    <td className="px-6 py-3 text-slate-500">{exp.category}</td>
                    <td className="px-6 py-3 text-slate-500 text-xs">
                      {exp.matter ? `${exp.matter.matter_number} · ${exp.matter.title}` : '—'}
                    </td>
                    <td className="px-6 py-3">
                      <Badge tone={exp.billable ? (exp.billed ? 'slate' : 'green') : 'slate'}>
                        {exp.billable ? (exp.billed ? 'Billed' : 'Billable') : 'Non-billable'}
                      </Badge>
                    </td>
                    <td className="px-6 py-3 text-right font-mono font-semibold">{formatMoney(exp.amount)}</td>
                    <td className="px-6 py-3 text-right">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openEdit(exp)} className="text-slate-400 hover:text-indigo-600 transition" title="Edit expense"><Pencil size={14} /></button>
                        <button onClick={() => setDeletingExpense(exp)} className="text-slate-400 hover:text-red-500 transition" title="Delete expense"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
      </Card>

      {/* Add / Edit Modal */}
      <Modal
        open={modalOpen}
        title={isEditing ? 'Edit expense' : 'Log expense'}
        onClose={closeModal}
        footer={
          <>
            <Button variant="ghost" onClick={closeModal}>Cancel</Button>
            <Button
              type="submit"
              form="expense-form"
              icon={isEditing ? undefined : Plus}
              disabled={saving}
            >
              {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Log expense'}
            </Button>
          </>
        }
      >
        <form id="expense-form" onSubmit={submit} className="space-y-4">
          <div>
            <Label>Description *</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              required
              placeholder="e.g. Filing fee – Superior Court"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Amount (GHS) *</Label>
              <Input
                type="number" min="0" step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                required placeholder="0.00"
              />
            </div>
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={form.expense_date}
                onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                required
              />
            </div>
          </div>
          <div>
            <Label>Category</Label>
            <Select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              options={CATEGORIES.map((c) => ({ value: c, label: c }))}
            />
          </div>
          <div>
            <Label>Case file (optional)</Label>
            <Select
              value={form.matter_id}
              onChange={(e) => setForm({ ...form, matter_id: e.target.value })}
              options={[{ value: '', label: 'None' }, ...matters.map((m) => ({ value: m.id, label: `${m.matter_number} — ${m.title}` }))]}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="billable"
              type="checkbox"
              checked={form.billable}
              onChange={(e) => setForm({ ...form, billable: e.target.checked })}
              className="rounded border-slate-300 text-indigo-600"
            />
            <label htmlFor="billable" className="text-sm text-slate-700 dark:text-slate-300">Billable to client</label>
          </div>
        </form>
      </Modal>

      <ConfirmDeleteModal
        open={!!deletingExpense}
        onClose={() => setDeletingExpense(null)}
        loading={false}
        title="Delete expense"
        description={<>Are you sure you want to delete this expense? <strong>{deletingExpense?.description}</strong></>}
        onConfirm={handleDeleteExpense}
      />
    </div>
  );
}
